import React, { useEffect, useState } from 'react';
import { api } from '../services/api';

function AccessControl() {
  const [whitelist, setWhitelist] = useState<any[]>([]);
  const [accessLog, setAccessLog] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [newIp, setNewIp] = useState('');
  const [newSystem, setNewSystem] = useState('dialedin');
  const [newLabel, setNewLabel] = useState('');
  const [logDate, setLogDate] = useState(new Date().toISOString().split('T')[0]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [wl, log] = await Promise.all([
        api.getWhitelist().catch(() => ({ entries: [] })),
        api.getAccessLog({ date: logDate }).catch(() => ({ entries: [] })),
      ]);
      setWhitelist(wl.entries || []);
      setAccessLog(log.entries || []);
    } catch (err) {
      console.error('Failed to load access data:', err);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [logDate]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newIp.trim()) return;
    try {
      await api.addWhitelist(newSystem, newIp.trim(), newLabel.trim());
      setNewIp('');
      setNewLabel('');
      loadData();
    } catch (err: any) {
      alert('Failed to add: ' + (err.message || 'Unknown error'));
    }
  };

  const handleRemove = async (systemId: string, ip: string) => {
    if (!window.confirm(`Remove ${ip} from ${systemId} whitelist?`)) return;
    try {
      await api.removeWhitelist(systemId, ip);
      loadData();
    } catch (err: any) {
      alert('Failed to remove: ' + (err.message || 'Unknown error'));
    }
  };

  const handleApprove = async (ip: string, systemId: string) => {
    const label = window.prompt(`Label for ${ip} (e.g., "DialedIn production server"):`);
    if (label === null) return;
    try {
      await api.approveAccess(ip, systemId, label);
      loadData();
    } catch (err: any) {
      alert('Failed to approve: ' + (err.message || 'Unknown error'));
    }
  };

  if (loading) return <div className="loading">Loading access control...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>Access Control</h1>
        <p>Manage IP whitelist and review connection attempts</p>
      </div>

      {/* Add to whitelist */}
      <div className="setup-card">
        <h3>Add IP to Whitelist</h3>
        <form onSubmit={handleAdd} className="search-form">
          <input
            type="text"
            placeholder="IP address (e.g., 203.0.113.50)"
            value={newIp}
            onChange={e => setNewIp(e.target.value)}
            style={{ minWidth: 200 }}
            required
          />
          <select value={newSystem} onChange={e => setNewSystem(e.target.value)}>
            <option value="dialedin">DialedIn</option>
            <option value="pulsedial">PulseDial</option>
            <option value="vicidial">Vicidial</option>
          </select>
          <input
            type="text"
            placeholder="Label (e.g., DialedIn production)"
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            style={{ minWidth: 200 }}
          />
          <button type="submit">Add</button>
        </form>
      </div>

      {/* Current whitelist */}
      <h2>Whitelisted IPs</h2>
      <table className="data-table">
        <thead>
          <tr>
            <th>IP Address</th>
            <th>System</th>
            <th>Label</th>
            <th>Added</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {whitelist.map((entry, i) => (
            <tr key={i}>
              <td><code>{entry.ip}</code></td>
              <td><span className={`system-tag ${entry.systemId}`}>{entry.systemId}</span></td>
              <td>{entry.label || '-'}</td>
              <td>{entry.addedAt ? new Date(entry.addedAt).toLocaleDateString() : '-'}</td>
              <td>
                <button
                  onClick={() => handleRemove(entry.systemId || entry.PK?.replace('WHITELIST#', ''), entry.ip)}
                  style={{ background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
          {whitelist.length === 0 && (
            <tr><td colSpan={5} style={{ textAlign: 'center', color: '#888' }}>No IPs whitelisted yet</td></tr>
          )}
        </tbody>
      </table>

      {/* Access log */}
      <h2 style={{ marginTop: 32 }}>Connection Log</h2>
      <div className="search-form">
        <input type="date" value={logDate} onChange={e => setLogDate(e.target.value)} />
      </div>
      <table className="data-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>IP Address</th>
            <th>System</th>
            <th>Status</th>
            <th>Details</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {accessLog.map((entry, i) => (
            <tr key={i}>
              <td>{entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString() : '-'}</td>
              <td><code>{entry.ip}</code></td>
              <td>{entry.systemId || '-'}</td>
              <td>
                <span className={`badge badge-${entry.status === 'allowed' ? 'success' : entry.status === 'blocked' ? 'danger' : 'warning'}`}>
                  {entry.status}
                </span>
              </td>
              <td>{entry.details || '-'}</td>
              <td>
                {(entry.status === 'blocked' || entry.status === 'unknown') && (
                  <button
                    onClick={() => handleApprove(entry.ip, entry.systemId || 'dialedin')}
                    style={{ background: '#27ae60', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}
                  >
                    Approve
                  </button>
                )}
              </td>
            </tr>
          ))}
          {accessLog.length === 0 && (
            <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888' }}>No connection attempts logged for this date</td></tr>
          )}
        </tbody>
      </table>

      {/* Instructions note */}
      <div className="setup-card" style={{ marginTop: 24 }}>
        <h3>How It Works</h3>
        <ul style={{ fontSize: 14, lineHeight: 1.8 }}>
          <li>When a new provider needs to send recordings, ask them for the <strong>IP address</strong> their server will connect from</li>
          <li>Add that IP to the whitelist above for the correct system</li>
          <li>Ask them to send a <strong>test file</strong> before going live</li>
          <li>If an unknown IP tries to connect, you'll get an <strong>email notification</strong> and it will appear in the log above</li>
          <li>You can <strong>approve</strong> an IP directly from the log with one click</li>
        </ul>
      </div>
    </div>
  );
}

export default AccessControl;
